// src/pages/Users.js
import { useEffect, useState } from 'react';
import { ref, onValue, set } from 'firebase/database';
import { rtdb } from '../firebase/config';
import { PageHeader, Card, Badge, Btn, Table, TD, SearchBar, FilterSelect, ConfirmModal } from '../components/UI';

export default function Users() {
  const [users, setUsers] = useState([]);
  const [blocked, setBlocked] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [confirm, setConfirm] = useState(null);

  // Realtime DB users
  useEffect(() => {
    const unsub = onValue(ref(rtdb, 'users'), snap => {
      if (snap.exists()) {
        const data = snap.val();
        setUsers(Object.entries(data).map(([id, val]) => ({ id, ...val })));
      } else {
        setUsers([]);
      }
      setLoading(false);
    }, () => setLoading(false));
    return () => unsub();
  }, []);

  // Realtime DB blocked_users
  useEffect(() => {
    const unsub = onValue(ref(rtdb, 'blocked_users'), snap => {
      setBlocked(snap.val() || {});
    });
    return () => unsub();
  }, []);

  const blockUser = async (phone, block) => {
    const clean = phone?.replace(/\D/g, '');
    if (!clean) return;
    await set(ref(rtdb, `blocked_users/${clean}`), block ? true : null);
    setConfirm(null);
  };

  const isBlocked = (phone) => {
    const clean = phone?.replace(/\D/g, '');
    return !!blocked[clean];
  };

  const filtered = users.filter(u => {
    const q = search.toLowerCase();
    const matchQ = !q || u.phone?.includes(q) || u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q);
    const bl = isBlocked(u.phone);
    const matchF = filter === 'all' || (filter === 'blocked' && bl) || (filter === 'active' && !bl);
    return matchQ && matchF;
  });

  // Export CSV
  const exportCSV = () => {
    const rows = [['Phone', 'Name', 'Email', 'Status', 'Registered']];
    filtered.forEach(u => rows.push([
      u.phone || '',
      u.name || '',
      u.email || '',
      isBlocked(u.phone) ? 'Blocked' : 'Active',
      u.createdAt ? new Date(u.createdAt).toLocaleDateString() : '',
    ]));
    const csv = rows.map(r => r.join(',')).join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    a.download = 'users.csv'; a.click();
  };

  const tableRows = filtered.map(u => {
    const bl = isBlocked(u.phone);
    return [
      <TD key="ph" mono>{u.phone || '—'}</TD>,
      <TD key="nm"><div style={{ fontWeight: 600 }}>{u.name || '—'}</div></TD>,
      <TD key="em">{u.email || '—'}</TD>,
      <TD key="st"><Badge status={bl ? 'blocked' : 'approved'} /></TD>,
      <TD key="dt">{u.createdAt ? new Date(u.createdAt).toLocaleDateString('bn-BD') : '—'}</TD>,
      <TD key="ac">
        {bl
          ? <Btn variant="ghost" onClick={() => setConfirm({ phone: u.phone, name: u.name || u.phone, block: false })}>↩ Unblock</Btn>
          : <Btn variant="danger" onClick={() => setConfirm({ phone: u.phone, name: u.name || u.phone, block: true })}>⊘ Block</Btn>
        }
      </TD>,
    ];
  });

  const totalBlocked = Object.values(blocked).filter(Boolean).length;

  return (
    <div>
      <PageHeader
        title="👥 User Management"
        sub={`মোট ${users.length} জন user · ${totalBlocked} জন blocked`}
        action={
          <Btn onClick={exportCSV} variant="ghost" size="sm">⬇ CSV Export</Btn>
        }
      />

      <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
        {[['all', 'সকল'], ['active', 'Active'], ['blocked', 'Blocked']].map(([v, l]) => (
          <button key={v} onClick={() => setFilter(v)} style={{
            padding: '4px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer',
            background: filter === v ? '#2271b1' : '#2a2a2a',
            color: filter === v ? '#fff' : '#a7aaad',
            border: filter === v ? '1px solid #2271b1' : '1px solid #3a3a3a',
          }}>{l}</button>
        ))}
      </div>

      <Card>
        <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
          <SearchBar value={search} onChange={setSearch} placeholder="ফোন, নাম বা email..." />
          <FilterSelect value={filter} onChange={setFilter} options={[['all', 'সকল'], ['active', 'Active'], ['blocked', 'Blocked']]} />
        </div>
        <div style={{ color: '#646970', fontSize: 12, marginBottom: 12 }}>{filtered.length} টি result</div>
        <Table cols={['Phone', 'নাম', 'Email', 'Status', 'তারিখ', 'Action']} rows={tableRows} loading={loading} />
      </Card>

      <ConfirmModal
        open={!!confirm}
        title={confirm?.block ? '⊘ Block করবেন?' : '↩ Unblock করবেন?'}
        msg={`"${confirm?.name}" কে ${confirm?.block ? 'block' : 'unblock'} করতে চান?`}
        onConfirm={() => blockUser(confirm.phone, confirm.block)}
        onCancel={() => setConfirm(null)}
        danger={confirm?.block}
      />
    </div>
  );
}